/**
 * Product Manager Component
 * 
 * Allows administrators to manage data products and their attributes.
 * Provides CRUD operations with deletion protection for products/attributes
 * that have associated themes.
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  SpaceBetween,
  Header,
  Container,
  Box,
  Alert,
  Modal,
  Form,
  FormField,
  Input,
  Textarea,
  Select,
  ExpandableSection,
  StatusIndicator,
  Flashbar,
  FlashbarProps,
} from '@cloudscape-design/components';
import {
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  addAttribute,
  updateAttribute,
  deleteAttribute,
} from '../../services/themeApi';
import { DataProduct, AttributeDefinition, DataType } from '../../types/themes';

interface ProductManagerProps {
  onProductsChange?: () => void;
}

const ProductManager: React.FC<ProductManagerProps> = ({ onProductsChange }) => {
  const [products, setProducts] = useState<DataProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [editingProduct, setEditingProduct] = useState<DataProduct | null>(null);
  const [editingAttribute, setEditingAttribute] = useState<{ productId: string; attribute: AttributeDefinition } | null>(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showAttributeForm, setShowAttributeForm] = useState<string | null>(null);

  // Form state
  const [productForm, setProductForm] = useState({
    product_id: '',
    display_name: '',
    description: '',
  });

  const [attributeForm, setAttributeForm] = useState({
    attribute_id: '',
    display_name: '',
    data_type: 'categorical' as DataType,
    unit: '',
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setFlashbarItems([]);
      const data = await listProducts();
      setProducts(data);
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to load products',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setFlashbarItems([]);
      await createProduct({
        product_id: productForm.product_id,
        display_name: productForm.display_name,
        description: productForm.description,
        attributes: [],
      });
      setProductForm({ product_id: '', display_name: '', description: '' });
      setShowProductForm(false);
      await loadProducts();
      onProductsChange?.();
      setFlashbarItems([{
        type: 'success',
        content: 'Product created successfully',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to create product',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    }
  };

  const handleUpdateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) return;

    try {
      setFlashbarItems([]);
      await updateProduct(editingProduct.product_id, {
        display_name: productForm.display_name,
        description: productForm.description,
        is_active: editingProduct.is_active,
      });
      setEditingProduct(null);
      setProductForm({ product_id: '', display_name: '', description: '' });
      await loadProducts();
      onProductsChange?.();
      setFlashbarItems([{
        type: 'success',
        content: 'Product updated successfully',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to update product',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm('Are you sure you want to delete this product? This will fail if any themes reference it.')) {
      return;
    }

    try {
      setFlashbarItems([]);
      await deleteProduct(productId);
      await loadProducts();
      onProductsChange?.();
      setFlashbarItems([{
        type: 'success',
        content: 'Product deleted successfully',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to delete product',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    }
  };

  const handleAddAttribute = async (e: React.FormEvent, productId: string) => {
    e.preventDefault();
    try {
      setFlashbarItems([]);
      await addAttribute(productId, {
        attribute_id: attributeForm.attribute_id,
        display_name: attributeForm.display_name,
        data_type: attributeForm.data_type,
        unit: attributeForm.unit || undefined,
      });
      setAttributeForm({ attribute_id: '', display_name: '', data_type: 'categorical', unit: '' });
      setShowAttributeForm(null);
      await loadProducts();
      onProductsChange?.();
      setFlashbarItems([{
        type: 'success',
        content: 'Attribute added successfully',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to add attribute',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    }
  };

  const handleUpdateAttribute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAttribute) return;

    try {
      setFlashbarItems([]);
      await updateAttribute(editingAttribute.productId, editingAttribute.attribute.attribute_id, {
        display_name: attributeForm.display_name,
        data_type: attributeForm.data_type,
        unit: attributeForm.unit || undefined,
      });
      setEditingAttribute(null);
      setAttributeForm({ attribute_id: '', display_name: '', data_type: 'categorical', unit: '' });
      await loadProducts();
      onProductsChange?.();
      setFlashbarItems([{
        type: 'success',
        content: 'Attribute updated successfully',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to update attribute',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    }
  };

  const handleDeleteAttribute = async (productId: string, attributeId: string) => {
    if (!confirm('Are you sure you want to delete this attribute? This will fail if any themes reference it.')) {
      return;
    }

    try {
      setFlashbarItems([]);
      await deleteAttribute(productId, attributeId);
      await loadProducts();
      onProductsChange?.();
      setFlashbarItems([{
        type: 'success',
        content: 'Attribute deleted successfully',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    } catch (err) {
      setFlashbarItems([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to delete attribute',
        dismissible: true,
        onDismiss: () => setFlashbarItems([]),
      }]);
    }
  };

  const startEditProduct = (product: DataProduct) => {
    setEditingProduct(product);
    setProductForm({
      product_id: product.product_id,
      display_name: product.display_name,
      description: product.description,
    });
    setShowProductForm(true);
  };

  const startEditAttribute = (productId: string, attribute: AttributeDefinition) => {
    setEditingAttribute({ productId, attribute });
    setAttributeForm({
      attribute_id: attribute.attribute_id,
      display_name: attribute.display_name,
      data_type: attribute.data_type,
      unit: attribute.unit || '',
    });
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setEditingAttribute(null);
    setShowProductForm(false);
    setShowAttributeForm(null);
    setProductForm({ product_id: '', display_name: '', description: '' });
    setAttributeForm({ attribute_id: '', display_name: '', data_type: 'categorical', unit: '' });
  };

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          <StatusIndicator type="loading">Loading products...</StatusIndicator>
        </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Flashbar items={flashbarItems} />

      <Header
        variant="h2"
        actions={
          <Button variant="primary" onClick={() => setShowProductForm(true)}>
            Create Product
          </Button>
        }
      >
        Data Products
      </Header>

      {/* Product Form Modal */}
      <Modal
        visible={showProductForm}
        onDismiss={cancelEdit}
        header={editingProduct ? 'Edit Product' : 'Create Product'}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={cancelEdit}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={() => {
                  if (editingProduct) {
                    handleUpdateProduct({ preventDefault: () => {} } as React.FormEvent);
                  } else {
                    handleCreateProduct({ preventDefault: () => {} } as React.FormEvent);
                  }
                }}
              >
                {editingProduct ? 'Update' : 'Create'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween size="m">
            <FormField label="Product ID" description="Unique identifier for the product">
              <Input
                value={productForm.product_id}
                onChange={({ detail }) => setProductForm({ ...productForm, product_id: detail.value })}
                disabled={!!editingProduct}
                placeholder="e.g., metar, marine, trail"
              />
            </FormField>
            <FormField label="Display Name" description="Human-readable name shown in the UI">
              <Input
                value={productForm.display_name}
                onChange={({ detail }) => setProductForm({ ...productForm, display_name: detail.value })}
                placeholder="e.g., METAR Weather Data"
              />
            </FormField>
            <FormField label="Description" description="Brief description of the data product">
              <Textarea
                value={productForm.description}
                onChange={({ detail }) => setProductForm({ ...productForm, description: detail.value })}
                rows={3}
                placeholder="Describe what this data product represents"
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>

      {/* Products List */}
      <SpaceBetween size="m">
        {products.map((product) => (
          <ExpandableSection
            key={product.product_id}
            headerText={product.display_name}
            headerDescription={product.description}
            headerActions={
              <SpaceBetween direction="horizontal" size="xs">
                <StatusIndicator type={product.is_active ? 'success' : 'stopped'}>
                  {product.is_active ? 'Active' : 'Inactive'}
                </StatusIndicator>
                <Button onClick={() => startEditProduct(product)}>Edit</Button>
                <Button onClick={() => handleDeleteProduct(product.product_id)}>Delete</Button>
              </SpaceBetween>
            }
          >
            <Container
              header={
                <Header
                  variant="h3"
                  actions={
                    <Button onClick={() => setShowAttributeForm(product.product_id)}>
                      Add Attribute
                    </Button>
                  }
                >
                  Attributes
                </Header>
              }
            >
              <SpaceBetween size="m">
                {/* Attribute Form */}
                {showAttributeForm === product.product_id && (
                  <Container>
                    <Form>
                      <SpaceBetween size="m">
                        <FormField label="Attribute ID">
                          <Input
                            value={attributeForm.attribute_id}
                            onChange={({ detail }) => setAttributeForm({ ...attributeForm, attribute_id: detail.value })}
                            placeholder="e.g., windSpeed, temperature"
                          />
                        </FormField>
                        <FormField label="Display Name">
                          <Input
                            value={attributeForm.display_name}
                            onChange={({ detail }) => setAttributeForm({ ...attributeForm, display_name: detail.value })}
                            placeholder="e.g., Wind Speed, Temperature"
                          />
                        </FormField>
                        <FormField label="Data Type">
                          <Select
                            selectedOption={{ label: attributeForm.data_type === 'categorical' ? 'Categorical' : 'Numeric', value: attributeForm.data_type }}
                            onChange={({ detail }) => setAttributeForm({ ...attributeForm, data_type: detail.selectedOption.value as DataType })}
                            options={[
                              { label: 'Categorical', value: 'categorical' },
                              { label: 'Numeric', value: 'numeric' },
                            ]}
                          />
                        </FormField>
                        <FormField label="Unit (optional)" description="e.g., kt, °F, ft">
                          <Input
                            value={attributeForm.unit}
                            onChange={({ detail }) => setAttributeForm({ ...attributeForm, unit: detail.value })}
                            placeholder="e.g., kt, °F, ft"
                          />
                        </FormField>
                        <Box float="right">
                          <SpaceBetween direction="horizontal" size="xs">
                            <Button onClick={() => setShowAttributeForm(null)}>Cancel</Button>
                            <Button variant="primary" onClick={(e) => handleAddAttribute(e as any, product.product_id)}>
                              Add
                            </Button>
                          </SpaceBetween>
                        </Box>
                      </SpaceBetween>
                    </Form>
                  </Container>
                )}

                {/* Edit Attribute Form */}
                {editingAttribute && editingAttribute.productId === product.product_id && (
                  <Container>
                    <Alert type="info">Editing attribute: {editingAttribute.attribute.attribute_id}</Alert>
                    <Form>
                      <SpaceBetween size="m">
                        <FormField label="Attribute ID">
                          <Input value={attributeForm.attribute_id} disabled />
                        </FormField>
                        <FormField label="Display Name">
                          <Input
                            value={attributeForm.display_name}
                            onChange={({ detail }) => setAttributeForm({ ...attributeForm, display_name: detail.value })}
                          />
                        </FormField>
                        <FormField label="Data Type">
                          <Select
                            selectedOption={{ label: attributeForm.data_type === 'categorical' ? 'Categorical' : 'Numeric', value: attributeForm.data_type }}
                            onChange={({ detail }) => setAttributeForm({ ...attributeForm, data_type: detail.selectedOption.value as DataType })}
                            options={[
                              { label: 'Categorical', value: 'categorical' },
                              { label: 'Numeric', value: 'numeric' },
                            ]}
                          />
                        </FormField>
                        <FormField label="Unit (optional)">
                          <Input
                            value={attributeForm.unit}
                            onChange={({ detail }) => setAttributeForm({ ...attributeForm, unit: detail.value })}
                          />
                        </FormField>
                        <Box float="right">
                          <SpaceBetween direction="horizontal" size="xs">
                            <Button onClick={cancelEdit}>Cancel</Button>
                            <Button 
                              variant="primary" 
                              onClick={() => handleUpdateAttribute({ preventDefault: () => {} } as React.FormEvent)}
                            >
                              Update
                            </Button>
                          </SpaceBetween>
                        </Box>
                      </SpaceBetween>
                    </Form>
                  </Container>
                )}

                {/* Attributes List */}
                {product.attributes.length === 0 ? (
                  <Box textAlign="center" color="text-body-secondary">
                    No attributes configured
                  </Box>
                ) : (
                  <SpaceBetween size="xs">
                    {product.attributes.map((attr) => (
                      <Container key={attr.attribute_id}>
                        <SpaceBetween direction="horizontal" size="m">
                          <Box>
                            <Box variant="strong">{attr.display_name}</Box>
                            <Box variant="small" color="text-body-secondary">
                              {attr.attribute_id} • {attr.data_type}
                              {attr.unit && ` • ${attr.unit}`}
                            </Box>
                          </Box>
                          <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                              <StatusIndicator type={attr.is_active ? 'success' : 'stopped'}>
                                {attr.is_active ? 'Active' : 'Inactive'}
                              </StatusIndicator>
                              <Button onClick={() => startEditAttribute(product.product_id, attr)}>
                                Edit
                              </Button>
                              <Button onClick={() => handleDeleteAttribute(product.product_id, attr.attribute_id)}>
                                Delete
                              </Button>
                            </SpaceBetween>
                          </Box>
                        </SpaceBetween>
                      </Container>
                    ))}
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </Container>
          </ExpandableSection>
        ))}
      </SpaceBetween>
    </SpaceBetween>
  );
};

export default ProductManager;
